import { Text, SimpleGrid, Box, Button } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';

import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface AdditionalFeaturesProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const AdditionalFeatures = ({ dwelling, index }: AdditionalFeaturesProps) => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const onSecuritySystemChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'security_system',
        value: {
          ...dwelling.rating_information.security_system,
          [field]: value,
        },
      }),
    );
  };

  const onSecurityRadioChange = (value: string, field: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'security_system',
        value: {
          ...dwelling.rating_information.security_system,
          [field]: value === 'true',
        },
      }),
    );
  };

  const onFireProtectionChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'fire_protection',
        value: {
          ...dwelling.rating_information.fire_protection,
          [field]: value,
        },
      }),
    );
  };

  const onBathroomsChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'bathrooms',
        value: {
          ...dwelling.rating_information.bathrooms,
          [field]: value,
        },
      }),
    );
  };

  const onKitchensChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'kitchens',
        value: {
          ...dwelling.rating_information.kitchens,
          [field]: value,
        },
      }),
    );
  };

  const onKitchenQualityChange = (event: any, itemIndex: number) => {
    const { value } = event.target;
    const currentItems = dwelling.rating_information.kitchens.quality || [];
    const updatedItems = [...currentItems];

    if (!updatedItems[itemIndex]) {
      updatedItems[itemIndex] = { value: '' };
    }

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      value: value,
    };

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'kitchens',
        value: {
          ...dwelling.rating_information.kitchens,
          quality: updatedItems,
        },
      }),
    );
  };

  const addKitchenQualityItem = () => {
    const currentItems = dwelling.rating_information.kitchens.quality || [];
    if (currentItems.length >= 2) return;
    const updatedItems = [...currentItems, { value: '' }];

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'kitchens',
        value: {
          ...dwelling.rating_information.kitchens,
          quality: updatedItems,
        },
      }),
    );
  };

  const removeKitchenQualityItem = (itemIndex: number) => {
    const currentItems = dwelling.rating_information.kitchens.quality || [];
    const updatedItems = currentItems.filter(
      (_: any, idx: number) => idx !== itemIndex,
    );

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'kitchens',
        value: {
          ...dwelling.rating_information.kitchens,
          quality: updatedItems,
        },
      }),
    );
  };

  const onGarageChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'garage_carport',
        value: {
          ...dwelling.rating_information.garage_carport,
          [field]: value,
        },
      }),
    );
  };

  const onSwimmingPoolChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'swimming_pool',
        value: {
          ...dwelling.rating_information.swimming_pool,
          [field]: value,
        },
      }),
    );
  };

  const onSwimmingPoolRadioChange = (value: string, field: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'swimming_pool',
        value: {
          ...dwelling.rating_information.swimming_pool,
          [field]: value === 'true',
        },
      }),
    );
  };

  const ratingInfo = dwelling.rating_information;

  if (!ratingInfo) return null;

  return (
    <Box>
      {/* Fire Protection */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Fire Protection
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Distance to Hydrant"
          name="fire_protection_distance_to_hydrant"
          value={ratingInfo.fire_protection.distance_to_hydrant || ''}
          onChange={(e: any) =>
            onFireProtectionChange(e, 'distance_to_hydrant')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Hydrant Type"
          name="fire_protection_hydrant_type"
          value={ratingInfo.fire_protection.hydrant_type || ''}
          onChange={(e: any) => onFireProtectionChange(e, 'hydrant_type')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Distance to Fire Hall"
          name="fire_protection_distance_to_fire_hall"
          value={
            ratingInfo.fire_protection.distance_to_responding_fire_hall || ''
          }
          onChange={(e: any) =>
            onFireProtectionChange(e, 'distance_to_responding_fire_hall')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Fire Hall Name"
          name="fire_protection_fire_hall_name"
          value={ratingInfo.fire_protection.fire_hall_name || ''}
          onChange={(e: any) => onFireProtectionChange(e, 'fire_hall_name')}
          disabled
        />
      </SimpleGrid>

      {/* Security System */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Security System
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Fire"
          name="security_system_fire"
          value={ratingInfo.security_system.fire || ''}
          onChange={(e: any) => onSecuritySystemChange(e, 'fire')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Burglary"
          name="security_system_burglary"
          value={ratingInfo.security_system.burglary || ''}
          onChange={(e: any) => onSecuritySystemChange(e, 'burglary')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Smoke Detectors"
          name="security_system_smoke_detectors"
          value={ratingInfo.security_system.smoke_detectors || ''}
          onChange={(e: any) => onSecuritySystemChange(e, 'smoke_detectors')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Smoke Detector Type"
          name="security_system_smoke_detector_type"
          value={ratingInfo.security_system.smoke_detector_type || ''}
          onChange={(e: any) =>
            onSecuritySystemChange(e, 'smoke_detector_type')
          }
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Number of Detectors"
          name="security_system_number_of_detectors"
          value={ratingInfo.security_system.number_of_detectors || ''}
          onChange={(e: any) =>
            onSecuritySystemChange(e, 'number_of_detectors')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Monitored By"
          name="security_system_monitored_by"
          value={ratingInfo.security_system.if_any_monitored_by || ''}
          onChange={(e: any) =>
            onSecuritySystemChange(e, 'if_any_monitored_by')
          }
        />
      </SimpleGrid>

      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Alarm Certificate Attached"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.security_system.is_alarm_certificate_attached,
          )}
          onChange={(value: string) =>
            onSecurityRadioChange(value, 'is_alarm_certificate_attached')
          }
          stacked
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Home Sprinklered"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(ratingInfo.security_system.has_home_sprinklered)}
          onChange={(value: string) =>
            onSecurityRadioChange(value, 'has_home_sprinklered')
          }
          stacked
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Premises Access Security"
          name="security_system_premises_access"
          value={ratingInfo.security_system.premises_access_security_type || ''}
          onChange={(e: any) =>
            onSecuritySystemChange(e, 'premises_access_security_type')
          }
        />
      </SimpleGrid>

      {/* Bathrooms */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Bathrooms
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Number of Full Bathrooms"
          name="bathrooms_full"
          value={ratingInfo.bathrooms.number_of_full || ''}
          onChange={(e: any) => onBathroomsChange(e, 'number_of_full')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Number of Half Bathrooms"
          name="bathrooms_half"
          value={ratingInfo.bathrooms.number_of_half || ''}
          onChange={(e: any) => onBathroomsChange(e, 'number_of_half')}
          disabled
        />
      </SimpleGrid>

      {/* Kitchens */}
      <Box mb="15px">
        <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
          Kitchens
        </Text>
        <SimpleGrid columns={6} spacing="15px" mb="15px">
          <FormInput
            section={SECTIONS.DWELLINGS}
            label="Number of Kitchens"
            name="kitchens_number"
            value={ratingInfo.kitchens.number_of_kitchens || ''}
            onChange={(e: any) => onKitchensChange(e, 'number_of_kitchens')}
            disabled
          />
        </SimpleGrid>

        {/* Kitchen Quality Items */}
        <Text fontWeight="medium" fontSize="sm" mb="10px" color="gray.600">
          Kitchen Quality
        </Text>
        {(ratingInfo.kitchens.quality || []).map(
          (item: any, itemIndex: number) => (
            <Box
              key={itemIndex}
              mb="10px"
              p="10px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="10px"
              >
                <Text fontSize="sm" color="gray.600">
                  Kitchen {itemIndex + 1} Quality
                </Text>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => removeKitchenQualityItem(itemIndex)}
                >
                  Remove
                </Button>
              </Box>
              <SimpleGrid columns={1} spacing="15px">
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Quality"
                  name={`kitchen_quality_${itemIndex}`}
                  value={item.value || ''}
                  onChange={(e: any) => onKitchenQualityChange(e, itemIndex)}
                />
              </SimpleGrid>
            </Box>
          ),
        )}

        {(ratingInfo.kitchens.quality || []).length < 2 && (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={addKitchenQualityItem}
            mb="15px"
            disabled={isDisabled}
          >
            Add Kitchen Quality ({(ratingInfo.kitchens.quality || []).length}
            /2)
          </Button>
        )}
      </Box>

      {/* Garage/Carport */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Garage/Carport
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Number of Cars"
          name="garage_number_of_cars"
          value={ratingInfo.garage_carport.number_of_cars || ''}
          onChange={(e: any) => onGarageChange(e, 'number_of_cars')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Garage Type"
          name="garage_type"
          value={ratingInfo.garage_carport.garage_type || ''}
          onChange={(e: any) => onGarageChange(e, 'garage_type')}
          disabled
        />
      </SimpleGrid>

      {/* Swimming Pool */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Swimming Pool
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Pool Year"
          name="swimming_pool_year"
          value={ratingInfo.swimming_pool.year || ''}
          onChange={(e: any) => onSwimmingPoolChange(e, 'year')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Pool Type"
          name="swimming_pool_type"
          value={ratingInfo.swimming_pool.pool_type || ''}
          onChange={(e: any) => onSwimmingPoolChange(e, 'pool_type')}
          disabled
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Pool Fenced"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(ratingInfo.swimming_pool.is_pool_fenced)}
          onChange={(value: string) =>
            onSwimmingPoolRadioChange(value, 'is_pool_fenced')
          }
          stacked
        />
      </SimpleGrid>
    </Box>
  );
};

export default AdditionalFeatures;
